/* eslint-disable react/jsx-pascal-case */
import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import Header from "design-system/src/Navigation/Header/Header";
import NavigationMenu from "design-system/src/Navigation/NavigationMenu/NavigationMenu";
import { useRollingStartNavigation } from "@src/lib/rolling-start-navigation";
import { SuspenseHelper } from "@src/components/general/SuspenseHelper/SuspenseHelper";
import { useAppSelector } from "@src/store/hooks";
import { _DefaultNoticeWrapper } from "@src/layout/DefaultLayout/DefaultHeader/DefaultNoticeWrapper/DefaultNoticeWrapper.styled";
import { useObservable } from "@legendapp/state/react";
import { enableReactComponents } from "@legendapp/state/config/enableReactComponents";
import LoginButtonFallback from "@src/components/feedback/LoginButton/LoginButtonFallback";
import MyCartMenuFallback from "./MyCartMenu/MyCartMenuFallback";
import SuspenseAfterSetup from "@src/lib/AfterSetup/SuspenseAfterSetup";
import { DefaultLayoutDatas } from "@src/layout/DefaultLayout/useDefaultLayoutDatas";
import SuspenseAfterInteraction from "@src/lib/AfterInteraction/SuspenseAfterInteraction";
import DeliveryOptionsListingFallback from "@src/components/molecule/DeliveryOptions/DeliveryOptionsListingFallback";
import StoreMenu from "@src/layout/DefaultLayout/DefaultHeader/StoreMenu/StoreMenu";
// CLS-FIX: sync comme StoreMenu — lazy+Suspense(null) effaçait le HTML SSR « Carta »
import CarteMenu from "./CarteMenu/CarteMenu";
// HYDRATION-FIX: sync comme StoreMenu/CarteMenu — lazy+Suspense faisait basculer
// le burger menu en rendu client si le chunk n'était pas chargé à temps
// (React #418/#421/#422, cf. SuspenseHelper.tsx).
import DefaultMenuDrawer from "./DefaultMenu/DefaultMenuDrawer";
// HYDRATION-FIX: sync comme StoreMenu/CarteMenu/DefaultMenuDrawer — composants
// petits et toujours visibles, pas d'intérêt à les lazy-charger vs le risque
// de race d'hydratation.
import ReorderMenu from "./ReorderMenu/ReorderMenu";
import TopBanner from "./TopBanner/TopBanner";
import { EDeliveryOptionsUpdate } from "@src/components/molecule/DeliveryOptions/DeliveryOptions";
import useNavigationUrlFactory from "@src/hooks/useNavigationUrlFactory";
import useImprovedScrollDirection from "@src/hooks/useImprovedScrollDirection";
import { EHappyHourLocation } from "@src/lib/happyHour/types/HappyHour.interface";
import Portal from "design-system/src/Components/Portal/Portal";
import runAfterLcp from "@src/utils/runAfterLcp";
import debounce from "lodash/debounce";

// TBT-FIX: `enableReactComponents` est un side-effect de `@legendapp/state` qui
// patche les composants React. On le fait au tout premier render plutôt qu'au
// module-load pour ne pas grossir l'évaluation initiale de `app.js`.
let __legendReactInitialized = false;
function ensureLegendReactComponents() {
  if (__legendReactInitialized) return;
  __legendReactInitialized = true;
  enableReactComponents();
}

const GATSBY_ENV_LEGACY = !!process.env.GATSBY_ENV_LEGACY;

export interface IDefaultHeaderProps {
  wrapper?: "listing" | "home" | "store-locator";
  datas: DefaultLayoutDatas;
  isModal?: boolean;
}
const LoginButton = React.lazy(
  () => import("@src/components/feedback/LoginButton/LoginButton")
);
const MyCartMenu = React.lazy(() => import("./MyCartMenu/MyCartMenu"));
const DeliveryOptions = lazy(
  () => import("@src/components/molecule/DeliveryOptions/DeliveryOptions")
);
const DiscountProductNotificationWrapper = lazy(
  () =>
    import(
      "@src/lib/DiscountProduct/DiscountProductNotificationWrapper/DiscountProductNotificationWrapper"
    )
);
const EndOfServiceWrapper = lazy(
  () =>
    import("@src/lib/end-of-service/EndOfServiceWrapper/EndOfServiceWrapper")
);

const DefaultHeader = (props: IDefaultHeaderProps) => {
  ensureLegendReactComponents();
  const { wrapper, datas } = props;
  const { menu_burger } = datas;
  const { navigate } = useRollingStartNavigation();
  const showTopBanner$ = useObservable({ showTopBanner: true });
  const routes = useNavigationUrlFactory();

  const isListing = wrapper === "listing";
  const isStoreLocator = wrapper === "store-locator";
  const isHome = wrapper === "home";

  const forceShowStatus = isListing || isStoreLocator;

  // CLS-FIX home: Redux starts deliveryOptionsVisible=true, then hero IO hides
  // it. Keep header middle unmounted until IO has hidden once; remount only
  // when visible again (scroll past hero) so enter animation can run.
  const [homeHeaderDeliveryUnlocked, setHomeHeaderDeliveryUnlocked] = useState(
    !isHome
  );

  const goToHome = () => {
    navigate(routes.home());
  };

  const isDeliveryOptionsVisible = useAppSelector(
    (state) => state.app.deliveryOptionsVisible
  );

  const isDeliveryOptionsDown = useAppSelector(
    (state) => state.app.deliveryOptionsDown
  );

  const hideTopDeliveryStatus = useAppSelector(
    ({ cart }) => cart.hideTopDeliveryStatus
  );

  const onDeliveryOptionsSuccess = useCallback(
    (status: EDeliveryOptionsUpdate) => {
      if (status === EDeliveryOptionsUpdate.CREATE && wrapper === "home") {
        navigate(routes.allCategories());
      }
    },
    [navigate, routes, wrapper]
  );

  useImprovedScrollDirection({
    threshold: 15,
    velocityThreshold: 0.3,
    enableMomentum: true,
    showAtTop: true,
    topThreshold: 64,
    useForceDispatch: false,
  });

  useEffect(() => {
    const handleTopBanner = () => {
      const scrollTop = document.documentElement.scrollTop;
      showTopBanner$.showTopBanner.set(scrollTop <= 0);
    };
    const debouncedHandleScroll = debounce(handleTopBanner, 200);
    let listenerAttached = false;

    const cancelSchedule = runAfterLcp(
      () => {
        document.addEventListener("scroll", debouncedHandleScroll, {
          passive: true,
        });
        listenerAttached = true;
        debouncedHandleScroll();
      },
      { timeout: 2500, fallbackDelay: 200 }
    );

    return () => {
      cancelSchedule();
      if (listenerAttached) {
        document.removeEventListener("scroll", debouncedHandleScroll);
      }
    };
  }, [showTopBanner$]);

  useEffect(() => {
    if (!isHome) return;
    if (!isDeliveryOptionsVisible) {
      setHomeHeaderDeliveryUnlocked(true);
    }
  }, [isHome, isDeliveryOptionsVisible]);

  const deliveryOptionsFallback = (
    <DeliveryOptionsListingFallback
      forceShowStatus={forceShowStatus || isHome}
    />
  );

  // Home: mount only after unlock + visible (scroll past hero). Listing: always.
  const showHomeHeaderDelivery =
    !isHome || (homeHeaderDeliveryUnlocked && isDeliveryOptionsVisible);

  const show = showTopBanner$.showTopBanner.get();
  return (
    <>
      <Header
        style={{
          ...(GATSBY_ENV_LEGACY && {
            display: "none",
          }),
          ...(isListing && {
            height: "auto",
          }),
          overflow: "visible",
        }}
        className={"default-header"}
        autoHeight={isListing}
        onClickLogo={goToHome}
        showTopBar={show}
        showDeliveryOptions={showHomeHeaderDelivery && isDeliveryOptionsVisible}
        isDeliveryOptionsDown={isDeliveryOptionsDown}
        topBarContent={
          <TopBanner
            location={
              isListing ? EHappyHourLocation.LISTING_DEFAULT : undefined
            }
            onToggleTopBanner={(v) => {
              showTopBanner$.showTopBanner.set(v);
            }}
          />
        }
        isHome={isHome}
        renderMenu={() => {
          return (
            <DefaultMenuDrawer widgetGroup={menu_burger}>
              {({ onOpen }) => (
                <NavigationMenu onClick={onOpen} icon="menu" />
              )}
            </DefaultMenuDrawer>
          );
        }}
        leftContent={({ fixMobileClass }) => (
          <>
            <CarteMenu
              className={fixMobileClass}
              disableDropDown={wrapper === "listing"}
            />
            <ReorderMenu />
            <StoreMenu />
          </>
        )}
        rightContent={
          <>
            <SuspenseAfterSetup fallback={<LoginButtonFallback />}>
              <SuspenseHelper fallback={<LoginButtonFallback />}>
                <LoginButton />
              </SuspenseHelper>
            </SuspenseAfterSetup>

            <SuspenseAfterInteraction fallback={<MyCartMenuFallback />}>
              <SuspenseHelper fallback={<MyCartMenuFallback />}>
                <MyCartMenu />
              </SuspenseHelper>
            </SuspenseAfterInteraction>
          </>
        }
        middleContent={
          showHomeHeaderDelivery && !hideTopDeliveryStatus ? (
            <SuspenseAfterSetup
              phase="initialized"
              // Home: no fallback pill (would flash before real mount).
              // Listing: keep skeleton while setup/chunk load.
              // phase initialized (pas ready) : setupReady peut bloquer si
              // id_store local sans store.sync — fallback non cliquable sinon.
              fallback={isHome ? null : deliveryOptionsFallback}
            >
              <Suspense fallback={isHome ? null : deliveryOptionsFallback}>
                <DeliveryOptions
                  forceShowStatus={forceShowStatus}
                  onSuccess={onDeliveryOptionsSuccess}
                />
              </Suspense>
            </SuspenseAfterSetup>
          ) : null
        }
        headerBottomContent={({ style, autoHeight, isMobile }) => (
          <_DefaultNoticeWrapper
            style={!autoHeight || isMobile ? undefined : style}
          >
            <SuspenseAfterSetup phase="initialized">
              <Suspense fallback={null}>
                <EndOfServiceWrapper
                  style={{
                    ...(isMobile ? style : {}),
                  }}
                  autoHeight={autoHeight}
                />
              </Suspense>
            </SuspenseAfterSetup>
            {isListing &&
              (!isMobile ? (
                <SuspenseAfterSetup phase="initialized">
                  <Suspense fallback={null}>
                    <DiscountProductNotificationWrapper
                      autoHeight={autoHeight}
                    />
                  </Suspense>
                </SuspenseAfterSetup>
              ) : (
                <Portal>
                  <SuspenseAfterSetup phase="initialized">
                    <Suspense fallback={null}>
                      <DiscountProductNotificationWrapper
                        autoHeight={false}
                      />
                    </Suspense>
                  </SuspenseAfterSetup>
                </Portal>
              ))}
          </_DefaultNoticeWrapper>
        )}
      ></Header>
    </>
  );
};

export default DefaultHeader;
